import type { MvcLocation } from "@repo/models";
import type { GridList } from "@repo/ui";
import type { UseQueryResult } from "@tanstack/react-query";

import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type { LocationsFilter } from "../../models";

import LocationsApi from "../../api/locationsApi";
import { QUERY_KEYS } from "../../api/queries";

const SLIM_SUPPORTED_PROPERTIES = new Set([]);

export function useLocationsData(filters: LocationsFilter) {
	const isSlimSupported = useMemo(() => {
		const sortBy = filters.sortBy?.name || (filters as Record<string, any>)["sortBy.name"];
		if (!sortBy) return true;
		return SLIM_SUPPORTED_PROPERTIES.has(sortBy);
	}, [filters]);

	const locationsSlimQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.Locations, "slim", filters],
		queryFn: ({ signal }) => LocationsApi.getLocations({ ...filters, isSlim: true }, { signal }),
		enabled: isSlimSupported,
	});

	const locationsFullQuery = useQuery({
		queryKey: [QUERY_KEYS.Locations.Locations, "full", filters],
		queryFn: ({ signal }) => LocationsApi.getLocations({ ...filters, isSlim: false }, { signal }),
	});

	const mergedData = useMemo(() => {
		if (!isSlimSupported) return locationsFullQuery.data;

		if (!locationsSlimQuery.data?.items) return undefined;
		if (!locationsFullQuery.data) {
			return {
				...locationsSlimQuery.data,
			};
		}

		return locationsFullQuery.data;
	}, [isSlimSupported, locationsSlimQuery.data, locationsFullQuery.data]);

	const locationsQueryProxy = useMemo(() => {
		const activeQuery = isSlimSupported ? locationsSlimQuery : locationsFullQuery;
		return {
			...activeQuery,
			data: mergedData,
			isEnriching: isSlimSupported && locationsFullQuery.isFetching,
		} as UseQueryResult<GridList<MvcLocation>> & { isEnriching: boolean };
	}, [isSlimSupported, locationsSlimQuery, locationsFullQuery, mergedData]);

	const refetch = useCallback(async () => {
		await locationsSlimQuery.refetch();
		await locationsFullQuery.refetch();
	}, [locationsSlimQuery, locationsFullQuery]);

	return {
		locationsQueryProxy,
		refetch,
	};
}
